/**
 * Which workspace an open plugin is acting in — and a way back when the user
 * has switched elsewhere since (ADR-022).
 *
 * A plugin's acting workspace is fixed when it opens. The host switcher is
 * global (shared across tabs), so without this a plugin left open in one tab
 * would silently start reading whichever workspace the user moved to in
 * another — somewhere they never chose to use it. Instead the plugin keeps
 * the workspace it opened in, says so, and offers to reopen in the new one.
 */

import { Building2, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';

export interface PluginWorkspaceIndicatorProps {
  /** The org workspace the plugin is pinned to; null = the user's Personal vault. */
  pinned: { holderId: string; displayName: string } | null;
  /** Where the host switcher points now, when that differs from `pinned`. */
  switchedTo: { displayName: string } | null;
  onReopen: () => void;
}

export function PluginWorkspaceIndicator({
  pinned,
  switchedTo,
  onReopen,
}: PluginWorkspaceIndicatorProps) {
  if (switchedTo) {
    return (
      <div
        role="status"
        className="absolute left-2 right-2 top-2 z-10 flex flex-wrap items-center gap-2 rounded-md border bg-background/95 px-3 py-2 text-sm shadow-sm"
      >
        <span className="min-w-0 flex-1">
          You switched to <strong>{switchedTo.displayName}</strong>. This app is still working in{' '}
          <strong>{pinned?.displayName ?? 'your Personal vault'}</strong>.
        </span>
        <Button size="sm" variant="outline" onClick={onReopen}>
          <RefreshCw className="mr-1 h-3.5 w-3.5" aria-hidden="true" />
          Reopen in {switchedTo.displayName}
        </Button>
      </div>
    );
  }
  if (!pinned) return null;
  return (
    <div
      className="pointer-events-none absolute bottom-2 left-2 z-10 flex max-w-[60%] items-center gap-1 rounded-full border bg-background/90 px-2 py-0.5 text-xs text-muted-foreground shadow-sm"
      title={`This app is working in ${pinned.displayName}`}
    >
      <Building2 className="h-3 w-3 shrink-0" aria-hidden="true" />
      <span className="truncate">Working in {pinned.displayName}</span>
    </div>
  );
}
